import { useState, useEffect, useCallback, useRef, FormEvent } from 'react';
import { api } from '../lib/api';
import AISlotSuggestions from '../components/dashboard/AISlotSuggestions';
import { Package, FileText, CheckCircle2, Loader2, Truck, User, Search, Layers, ChevronDown, ChevronRight, Clock, Info } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuthStore } from '../hooks/useAuth';
import { Combobox } from '../components/ui/Combobox';
import { CalendarSlotPicker } from '../components/booking/CalendarSlotPicker';
import { useLocation } from 'wouter';
import { validatePhone } from '../utils/fieldValidation';
import { formatInWarehouseTimeZone, formatLocalWallClock } from '../utils/timezoneDisplay';
import { DateTime } from 'luxon';

interface Warehouse {
    id: string;
    name: string;
    city: string;
    state: string;
    timeZone?: string;
}

export function AppointmentBooking() {
    const { user } = useAuthStore();
    const [, setLocation] = useLocation();
    const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
    const [loading, setLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    // Search State
    const [customers, setCustomers] = useState<any[]>([]);
    const [pos, setPos] = useState<any[]>([]);
    const [poLoading, setPoLoading] = useState(false);

    // Form State
    const [formData, setFormData] = useState({
        warehouseId: user?.warehouseId || '', // Default to user's warehouse if available
        startAt: '',
        endAt: '',
        // Canonical UTC occurrences — disambiguates DST fall-back repeated hours.
        startUtc: '',
        endUtc: '',
        customerPoNumber: '',
        truckNumber: '',
        trailerNumber: '',
        estimatedPalletCount: 0,
        notes: '',
        // Warehouse-only fields (optional)
        carrierName: '',
        carrierMcNumber: '',
        carrierEmail: '',
        carrierPhone: '',
        driverName: '',
        trailerType: '',
        // New Fields
        customerId: '',
        totalWeight: 0,
        // Phase 1: Multi-PO
        selectedPOs: [] as { id: string; poNumber: string; clientName: string; pallets: number; weight: number }[],
        // Phase 4B: Load reference
        loadId: '',
    });

    // Phase 4B: Load pre-fill from query params
    const [loadPrefill, setLoadPrefill] = useState<{ loadNumber: string } | null>(null);

    // Collapse state for progressive disclosure sections
    const [carrierExpanded, setCarrierExpanded] = useState(false);
    const [optionalExpanded, setOptionalExpanded] = useState(false);

    // UX-9: Edit mode detection via appointmentId query param
    const [editAppointmentId, setEditAppointmentId] = useState<string | null>(null);
    const isEditingAppointment = !!editAppointmentId;

    // UX-9: Track original values for change indicators
    const originalValuesRef = useRef<{ startAt: string; carrierName: string; selectedPOIds: string[] } | null>(null);

    // UX-9: Detect edit mode from query params
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const editId = params.get('appointmentId') || params.get('edit');
        if (editId) {
            setEditAppointmentId(editId);
        }
    }, []);

    // UX-9: Snapshot original values once form is populated (after prefill)
    useEffect(() => {
        if (isEditingAppointment && formData.startAt && !originalValuesRef.current) {
            originalValuesRef.current = {
                startAt: formData.startAt,
                carrierName: formData.carrierName,
                selectedPOIds: formData.selectedPOs.map(p => p.id),
            };
        }
    }, [isEditingAppointment, formData.startAt, formData.carrierName, formData.selectedPOs]);

    // UX-9: Compute which fields changed
    const changedFields = isEditingAppointment && originalValuesRef.current ? {
        timeChanged: formData.startAt !== originalValuesRef.current.startAt && !!formData.startAt,
        carrierChanged: formData.carrierName !== originalValuesRef.current.carrierName && !!formData.carrierName,
        posChanged: JSON.stringify(formData.selectedPOs.map(p => p.id).sort()) !== JSON.stringify(originalValuesRef.current.selectedPOIds.sort()),
    } : { timeChanged: false, carrierChanged: false, posChanged: false };

    const isWarehouseUser = user?.role && ['warehouse_manager', 'warehouse_staff', 'admin'].includes(user.role);

    useEffect(() => {
        loadWarehouses();
        if (isWarehouseUser && formData.warehouseId) {
            loadCustomers();
        }
    }, [isWarehouseUser, formData.warehouseId]);

    // Phase 4B: Parse loadId from query params and fetch prefill data
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const loadIdParam = params.get('loadId');
        if (loadIdParam && isWarehouseUser && user?.effectiveTier === 'operations') {
            setFormData(prev => ({ ...prev, loadId: loadIdParam }));
            api.request<{ loadId: string, loadNumber: string, prefill: any }>(`/loads/${loadIdParam}/schedule`, { method: 'POST' })
                .then(async (res) => {
                    setLoadPrefill({ loadNumber: res.loadNumber });
                    const p = res.prefill;
                    setFormData(prev => ({
                        ...prev,
                        loadId: loadIdParam,
                        carrierName: p.carrierName || prev.carrierName,
                        carrierMcNumber: p.carrierMcNumber || prev.carrierMcNumber,
                        carrierEmail: p.carrierEmail || prev.carrierEmail,
                        carrierPhone: p.carrierPhone || prev.carrierPhone,
                        driverName: p.driverName || prev.driverName,
                        truckNumber: p.truckNumber || prev.truckNumber,
                        trailerNumber: p.trailerNumber || prev.trailerNumber,
                        trailerType: p.trailerType || prev.trailerType,
                        estimatedPalletCount: p.estimatedPalletCount || prev.estimatedPalletCount,
                    }));

                    // Use PO details directly from schedule endpoint (no search needed)
                    if (p.poDetails && p.poDetails.length > 0) {
                        const newPOs = p.poDetails.map((po: any) => ({
                            id: po.id,
                            poNumber: po.poNumber,
                            clientName: po.clientName || 'Unknown',
                            pallets: po.pallets || 0,
                            weight: po.weight || 0,
                        }));
                        const totalPallets = newPOs.reduce((s: number, p: any) => s + p.pallets, 0);
                        setFormData(prev => ({
                            ...prev,
                            selectedPOs: newPOs,
                            customerPoNumber: newPOs[0]?.poNumber || '',
                            estimatedPalletCount: totalPallets || prev.estimatedPalletCount,
                        }));
                    }
                })
                .catch(err => console.error('[Load prefill] Failed:', err));
        }
    }, [isWarehouseUser]);

    // Auto-expand carrier section if load prefill didn't fill carrier
    useEffect(() => {
        if (loadPrefill && !formData.carrierName) {
            setCarrierExpanded(true);
        }
    }, [loadPrefill]);

    const loadWarehouses = async () => {
        setLoading(true);
        try {
            const response = await api.getWarehouses();
            setWarehouses(response.warehouses);
        } catch (error: any) {
            console.error('Failed to load warehouses:', error);
            toast.error('Failed to load warehouses. Please refresh.');
        } finally {
            setLoading(false);
        }
    };

    const loadCustomers = async () => {
        try {
            const res = await api.getCustomers(formData.warehouseId);
            setCustomers(res.customers || []);
        } catch (error) {
            console.error("Failed to load customers", error);
        }
    };

    const searchPOs = useCallback(async (query: string, customerId?: string) => {
        if (!formData.warehouseId) return;
        setPoLoading(true);
        try {
            const res = await api.searchWarehousePOs(query, customerId, true); // exclude closed
            setPos(res.results || []);
        } catch (error) {
            console.error("Failed to search POs", error);
        } finally {
            setPoLoading(false);
        }
    }, [formData.warehouseId]);

    // Initial PO load (recent)
    useEffect(() => {
        if (isWarehouseUser && formData.warehouseId) {
            searchPOs('', '');
        }
    }, [isWarehouseUser, formData.warehouseId, searchPOs]);

    // Auto-fill Logic
    // Auto-fill Logic
    const handlePOSelect = (poInput: string) => {
        const selectedPO = pos.find(p => p.id === poInput);

        if (selectedPO) {
            // Prevent duplicates
            if (formData.selectedPOs.some(p => p.id === selectedPO.id)) {
                toast.error('PO already added', { icon: '⚠️' });
                return;
            }
            const newPO = {
                id: selectedPO.id,
                poNumber: selectedPO.customerPoNumber,
                clientName: selectedPO.customerName || selectedPO.clientName || 'Unknown',
                pallets: selectedPO.totalSkids || selectedPO.palletCount || 0,
                weight: selectedPO.totalWeight || 0,
            };
            const updated = [...formData.selectedPOs, newPO];
            const totalPallets = updated.reduce((s, p) => s + p.pallets, 0);
            const totalWeight = updated.reduce((s, p) => s + p.weight, 0);
            setFormData(prev => ({
                ...prev,
                selectedPOs: updated,
                customerPoNumber: updated[0]?.poNumber || '', // Primary PO label
                estimatedPalletCount: totalPallets,
                totalWeight,
                notes: selectedPO.description ? `${prev.notes ? prev.notes + '\n' : ''}PO Note: ${selectedPO.description}` : prev.notes
            }));
            toast.success(`PO ${selectedPO.customerPoNumber} added`, { icon: '🚀' });
        }
    };

    const handlePORemove = (poId: string) => {
        const updated = formData.selectedPOs.filter(p => p.id !== poId);
        const totalPallets = updated.reduce((s, p) => s + p.pallets, 0);
        const totalWeight = updated.reduce((s, p) => s + p.weight, 0);
        setFormData(prev => ({
            ...prev,
            selectedPOs: updated,
            customerPoNumber: updated[0]?.poNumber || '',
            estimatedPalletCount: totalPallets,
            totalWeight,
        }));
    };

    // Warehouse-local YYYY-MM-DD for native input minimums
    const getLocalYYYYMMDD = () => {
        // Fallback to UTC if timezone is missing to avoid browser timezone shift
        const tz = user?.timeZone || 'UTC';
        return DateTime.now().setZone(tz).toISODate() || '';
    };



    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        if (!formData.warehouseId || !formData.startAt || !formData.endAt) {
            toast.error('Please fill in all required fields.');
            return;
        }

        // Carrier phone validation
        const errors: Record<string, string> = {};
        const phoneErr = validatePhone(formData.carrierPhone);
        if (phoneErr) errors.carrierPhone = phoneErr;
        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            toast.error('Please fix invalid fields');
            return;
        }
        setFieldErrors({});

        setIsSubmitting(true);
        try {
            const { selectedPOs, startAt, endAt, startUtc, endUtc, ...payload } = formData;
            
            const [scheduledDate, scheduledTime] = startAt ? startAt.split('T') : ['', ''];
            const [scheduledEndDate, scheduledEndTime] = endAt ? endAt.split('T') : ['', ''];

            await api.createAppointment({
                ...payload,
                poIds: selectedPOs.map(p => p.id), // Phase 1: Multi-PO
                scheduledDate,
                scheduledTime,
                // Canonical UTC — required to unambiguously resolve DST fall-back repeated hours.
                scheduledUtc: startUtc || undefined,
                scheduledEndDate,
                scheduledEndTime,
                scheduledEndUtc: endUtc || undefined,
                estimatedPalletCount: formData.estimatedPalletCount || undefined,
                carrierName: formData.carrierName || undefined,
                carrierMcNumber: formData.carrierMcNumber || undefined,
                carrierEmail: formData.carrierEmail || undefined,
                carrierPhone: formData.carrierPhone || undefined,
                driverName: formData.driverName || undefined,
                trailerType: formData.trailerType || undefined,
                loadId: formData.loadId || undefined, // Phase 4B
            });

            toast.success(
                isEditingAppointment
                    ? changedFields.timeChanged
                        ? 'Appointment updated — Carrier notified of new arrival time'
                        : 'Appointment updated — Dock board and calendar refreshed'
                    : 'Appointment scheduled — Now on dock board and calendar',
                { duration: 5000, icon: isEditingAppointment ? '✏️' : '🎉' }
            );

            // Redirect to dashboard — preserve onboarding context if applicable
            const isFromOnboarding = new URLSearchParams(window.location.search).get('from') === 'onboarding';
            setTimeout(() => setLocation(isFromOnboarding ? '/dashboard?from=onboarding' : '/dashboard'), 1000);

        } catch (err: any) {
            toast.error(err.message || 'Failed to create appointment');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleChange = (e: any) => {
        const { name, value, type } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'number' ? parseInt(value) || 0 : value,
        }));
    };

    const handleSlotSelect = (start: string, end: string, startUtc?: string, endUtc?: string) => {
        setFormData((prev) => ({
            ...prev,
            startAt: start,
            endAt: end,
            // Preserve canonical UTC occurrence so the payload can unambiguously identify which
            // repeated hour was selected during a DST fall-back window.
            startUtc: startUtc || '',
            endUtc: endUtc || '',
        }));
        toast.success('Time slot applied!', { icon: '✨', position: 'bottom-center' });
    };

    return (
        <div className="min-h-screen p-4 sm:p-6 max-w-7xl mx-auto space-y-6 animate-fade-in pb-20 mob-header-pt">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-foreground tracking-tight">
                    {isEditingAppointment ? 'Edit Appointment' : 'Book Appointment'}
                </h1>
                <p className="text-sm text-muted-foreground mt-0.5">Schedule when this truck is expected to arrive at your dock.</p>
                <p className="text-xs text-muted-foreground/70 mt-0.5">Appointments appear on the dock board and calendar automatically.</p>
            </div>

            {/* UX-9: Edit-mode header context strip */}
            {isEditingAppointment && (
                <div className="flex items-start gap-2.5 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800/40 rounded-lg px-3 py-2">
                    <Info className="w-3.5 h-3.5 text-indigo-500 shrink-0 mt-0.5" />
                    <div>
                        <p className="text-xs font-medium text-indigo-700 dark:text-indigo-300">Editing existing appointment</p>
                        <p className="text-[11px] text-indigo-600/70 dark:text-indigo-400/70 mt-0.5">Changes update the dock board and notify the carrier automatically.</p>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* LEFT: Main Booking Form (8 cols) */}
                <div className="lg:col-span-8 space-y-5">
                    <form
                        onSubmit={handleSubmit}
                        className="bg-card border border-border rounded-xl p-5 lg:p-6 shadow-sm"
                    >
                        {/* Phase 4B: Load reference banner */}
                        {loadPrefill && (
                            <div className="mb-5 p-3 bg-indigo-500/10 border border-indigo-500/25 rounded-lg flex items-center gap-3">
                                <div className="p-1.5 bg-indigo-500/15 rounded-md">
                                    <Layers className="text-indigo-400" size={16} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-semibold text-indigo-300">Scheduling from Load</div>
                                    <div className="text-xs text-indigo-400/70 font-mono">{loadPrefill.loadNumber}</div>
                                </div>
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-500/15 text-indigo-300 font-bold shrink-0">
                                    Pre-filled
                                </span>
                            </div>
                        )}

                        {/* Warehouse selector — prerequisite, always visible */}
                        <div className="mb-5">
                            <div className="space-y-1.5">
                                <label htmlFor="warehouseId" className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                    Facility <span className="text-red-400">*</span>
                                </label>
                                <div className="relative">
                                    <select
                                        id="warehouseId"
                                        name="warehouseId"
                                        className="w-full bg-background border border-border hover:border-border/80 focus:border-primary focus:ring-1 focus:ring-primary rounded-lg px-4 py-2.5 text-foreground appearance-none transition-colors"
                                        value={formData.warehouseId}
                                        onChange={(e) => {
                                            const { value } = e.target;
                                            setFormData(prev => ({
                                                ...prev,
                                                warehouseId: value,
                                                startAt: '',
                                                startUtc: '',
                                                endAt: '',
                                                endUtc: ''
                                            }));
                                        }}
                                        required
                                        disabled={loading || (isWarehouseUser && !!user?.warehouseId)}
                                    >
                                        <option value="">Select a facility...</option>
                                        {warehouses.map((warehouse) => (
                                            <option key={warehouse.id} value={warehouse.id}>
                                                {warehouse.name} ({warehouse.city}, {warehouse.state})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="h-px bg-border my-5" />

                        {/* ─── Step 1 — Shipment ─── */}
                        <div className="mb-6">
                            <h3 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
                                <span className="text-[10px] font-bold uppercase tracking-widest text-primary/60 self-center">Step 1</span>
                                <span className="text-muted-foreground/30">—</span>
                                <Package className="w-4 h-4 text-blue-400" />
                                Shipment
                            </h3>

                            {isWarehouseUser && (
                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                                                <User className="w-4 h-4 text-slate-400" />
                                                Customer
                                            </label>
                                            <Combobox
                                                placeholder="Select Customer..."
                                                options={customers.map(c => ({ value: c.id, label: c.name }))}
                                                value={formData.customerId}
                                                onChange={(val) => {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        customerId: val,
                                                        selectedPoId: '',
                                                        customerPoNumber: '',
                                                        estimatedPalletCount: 0,
                                                        totalWeight: 0
                                                    }));
                                                    searchPOs('', val);
                                                }}
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                                                <Search className="w-4 h-4 text-slate-400" />
                                                PO Lookup
                                            </label>
                                            <Combobox
                                                placeholder="Search PO Number..."
                                                loading={poLoading}
                                                options={pos
                                                    .filter(p => !formData.selectedPOs.some(sp => sp.id === p.id))
                                                    .map(p => ({
                                                        value: p.id,
                                                        label: `${p.customerPoNumber} — ${p.customerName || p.clientName || 'Unknown'}`,
                                                        description: `${p.totalSkids || 0} skids • ${p.totalWeight || 0} lbs`
                                                    }))}
                                                value=""
                                                onChange={handlePOSelect}
                                                onSearch={(q) => searchPOs(q, formData.customerId)}
                                                emptyText="No POs found. Type to search."
                                            />
                                        </div>
                                    </div>

                                    {/* Linked POs chips + inline confirmation */}
                                    {formData.selectedPOs.length > 0 && (
                                        <div className="p-3 bg-emerald-500/5 border border-emerald-500/15 rounded-lg">
                                            <div className="flex items-center gap-2 mb-2">
                                                <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                                                <span className="text-sm font-medium text-emerald-400">
                                                    {formData.selectedPOs.length === 1
                                                        ? `PO ${formData.selectedPOs[0].poNumber} linked`
                                                        : `${formData.selectedPOs.length} POs linked`
                                                    }
                                                </span>
                                            </div>
                                            <div className="flex flex-wrap gap-1.5">
                                                {formData.selectedPOs.map((po) => (
                                                    <span
                                                        key={po.id}
                                                        className="inline-flex items-center gap-1 px-2.5 py-1 bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 rounded-md text-xs font-mono"
                                                    >
                                                        {po.poNumber}
                                                        <span className="text-muted-foreground">• {po.clientName}</span>
                                                        <button
                                                            type="button"
                                                            onClick={() => handlePORemove(po.id)}
                                                            className="ml-0.5 text-muted-foreground hover:text-red-400 transition-colors"
                                                            aria-label={`Remove PO ${po.poNumber}`}
                                                        >
                                                            ×
                                                        </button>
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Empty-state helper — PO/Load missing fallback */}
                                    {formData.selectedPOs.length === 0 && !loadPrefill && (
                                        <p className="text-xs text-muted-foreground">You can schedule this now and attach a PO or load later.</p>
                                    )}

                                    {/* Primary PO read-only */}
                                    {formData.selectedPOs.length > 0 && (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="space-y-1.5">
                                                <label className="text-xs font-medium text-muted-foreground">
                                                    Primary PO
                                                </label>
                                                <div className="w-full bg-muted/50 border border-border rounded-lg px-3 py-2 text-sm text-muted-foreground cursor-not-allowed">
                                                    {formData.customerPoNumber || 'Selected via Lookup above'}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {!isWarehouseUser && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <div className="space-y-2">
                                        <label htmlFor="customerPoNumber" className="text-sm font-medium text-slate-300">
                                            PO Number <span className="text-xs text-slate-500">(Manual Entry)</span>
                                        </label>
                                        <input
                                            id="customerPoNumber"
                                            name="customerPoNumber"
                                            type="text"
                                            className="w-full bg-slate-950 border border-slate-700 hover:border-slate-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 rounded-lg px-4 py-2.5 text-white placeholder-slate-600 transition-colors"
                                            value={formData.customerPoNumber}
                                            onChange={handleChange}
                                            placeholder="Enter PO Number..."
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="h-px bg-border my-6" />

                        {/* ─── Step 2 — Select Time ─── */}
                        <div className="mb-6">
                            <h3 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
                                <span className="text-[10px] font-bold uppercase tracking-widest text-primary/60 self-center">Step 2</span>
                                <span className="text-muted-foreground/30">—</span>
                                <Clock className="w-4 h-4 text-primary" />
                                Select Time
                            </h3>
                            <p className="text-xs text-muted-foreground mb-3 -mt-1">This is when the truck is expected to arrive.</p>

                            {isWarehouseUser && formData.warehouseId ? (
                                <div className="space-y-2 animate-fade-in">
                                    <CalendarSlotPicker
                                        warehouseId={formData.warehouseId}
                                        onSelect={handleSlotSelect}
                                    />
                                    {formData.startAt && (
                                        <div className="flex items-center gap-2 mt-2 p-2.5 bg-primary/5 border border-primary/15 rounded-lg">
                                            <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                                            <span className="text-sm font-medium text-primary">
                                                {(() => {
                                                    const selectedWarehouseTimeZone = warehouses.find(w => w.id === formData.warehouseId)?.timeZone;
                                                    
                                                    // Format start
                                                    let startDisplay = '';
                                                    if (formData.startUtc) {
                                                        startDisplay = formatInWarehouseTimeZone(formData.startUtc, selectedWarehouseTimeZone, { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
                                                    } else if (formData.startAt) {
                                                        startDisplay = formatLocalWallClock(formData.startAt, "ccc, LLL d, h:mm a");
                                                    }

                                                    // Format end
                                                    let endDisplay = '';
                                                    if (formData.endUtc) {
                                                        endDisplay = formatInWarehouseTimeZone(formData.endUtc, selectedWarehouseTimeZone, { hour: '2-digit', minute: '2-digit' });
                                                    } else if (formData.endAt) {
                                                        endDisplay = formatLocalWallClock(formData.endAt, "h:mm a");
                                                    }
                                                    
                                                    return `${startDisplay} — ${endDisplay}`;
                                                })()}
                                            </span>
                                        </div>
                                    )}
                                    {/* UX-9: Time change indicator (edit mode only) */}
                                    {isEditingAppointment && changedFields.timeChanged && (
                                        <p className="text-[11px] text-indigo-600 dark:text-indigo-400 mt-1">
                                            ✎ Time updated — dock compatibility will be re-evaluated after saving
                                        </p>
                                    )}
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <div className="space-y-2">
                                        <label htmlFor="startAt" className="text-sm font-medium text-slate-300">
                                            Start Time <span className="text-red-400">*</span>
                                        </label>
                                        <input
                                            id="startAt"
                                            name="startAt"
                                            type="datetime-local"
                                            className="w-full bg-slate-950 border border-slate-700 hover:border-slate-600 focus:border-primary focus:ring-1 focus:ring-primary rounded-lg px-4 py-2.5 text-white placeholder-slate-500 transition-colors [color-scheme:dark]"
                                            value={formData.startAt}
                                            onChange={handleChange}
                                            min={`${getLocalYYYYMMDD()}T00:00`}
                                            required
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label htmlFor="endAt" className="text-sm font-medium text-slate-300">
                                            End Time <span className="text-red-400">*</span>
                                        </label>
                                        <input
                                            id="endAt"
                                            name="endAt"
                                            type="datetime-local"
                                            className="w-full bg-slate-950 border border-slate-700 hover:border-slate-600 focus:border-primary focus:ring-1 focus:ring-primary rounded-lg px-4 py-2.5 text-white placeholder-slate-500 transition-colors [color-scheme:dark]"
                                            value={formData.endAt}
                                            onChange={handleChange}
                                            min={formData.startAt || `${getLocalYYYYMMDD()}T00:00`}
                                            required
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* ─── Step 3 — Carrier (collapsible, warehouse only) ─── */}
                        {isWarehouseUser && (
                            <>
                                <div className="h-px bg-border my-6" />
                                <div className="mb-6">
                                    <button
                                        type="button"
                                        onClick={() => setCarrierExpanded(!carrierExpanded)}
                                        className="w-full text-base font-semibold text-foreground flex items-center gap-2 hover:text-foreground/80 transition-colors text-left mb-3"
                                    >
                                        <span className="text-[10px] font-bold uppercase tracking-widest text-primary/60 self-center">Step 3</span>
                                        <span className="text-muted-foreground/30">—</span>
                                        <Truck className="w-4 h-4 text-indigo-400" />
                                        Carrier
                                        <span className="ml-auto flex items-center gap-1.5">
                                            {!carrierExpanded && formData.carrierName && (
                                                <span className="text-xs text-muted-foreground font-normal">{formData.carrierName}</span>
                                            )}
                                            {carrierExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                                        </span>
                                    </button>

                                    {/* Inline carrier summary when collapsed and auto-filled */}
                                    {!carrierExpanded && formData.carrierName && (
                                        <div className="flex items-center gap-2.5 text-sm text-muted-foreground bg-muted/30 border border-border rounded-lg px-3 py-2">
                                            <Truck className="w-3.5 h-3.5 text-indigo-400 shrink-0" />
                                            <span className="text-foreground/80 font-medium text-sm">{formData.carrierName}</span>
                                            {formData.carrierMcNumber && <span className="text-xs text-muted-foreground">MC: {formData.carrierMcNumber}</span>}
                                        </div>
                                    )}

                                    {carrierExpanded && (
                                        <div className="space-y-4 animate-fade-in">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                                <div className="space-y-2">
                                                    <label htmlFor="carrierName" className="text-sm font-medium text-slate-300">Carrier Name</label>
                                                    <input id="carrierName" name="carrierName" type="text" className="w-full bg-slate-950 border border-slate-700 hover:border-slate-600 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded-lg px-4 py-2.5 text-white placeholder-slate-600 transition-colors" value={formData.carrierName} onChange={handleChange} placeholder="Carrier Company Name" />
                                                </div>
                                                <div className="space-y-2">
                                                    <label htmlFor="carrierMcNumber" className="text-sm font-medium text-slate-300">MC Number</label>
                                                    <input id="carrierMcNumber" name="carrierMcNumber" type="text" className="w-full bg-slate-950 border border-slate-700 hover:border-slate-600 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded-lg px-4 py-2.5 text-white placeholder-slate-600 transition-colors" value={formData.carrierMcNumber} onChange={handleChange} placeholder="e.g. 123456" />
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                                <div className="space-y-2">
                                                    <label htmlFor="carrierEmail" className="text-sm font-medium text-slate-300">Carrier Email</label>
                                                    <input id="carrierEmail" name="carrierEmail" type="email" className="w-full bg-slate-950 border border-slate-700 hover:border-slate-600 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded-lg px-4 py-2.5 text-white placeholder-slate-600 transition-colors" value={formData.carrierEmail} onChange={handleChange} placeholder="dispatch@carrier.com" />
                                                </div>
                                                <div className="space-y-2">
                                                    <label htmlFor="carrierPhone" className="text-sm font-medium text-slate-300">Carrier Phone</label>
                                                    <input id="carrierPhone" name="carrierPhone" type="tel" className={`w-full bg-slate-950 border ${fieldErrors.carrierPhone ? 'border-red-500 focus:ring-red-500' : 'border-slate-700 hover:border-slate-600 focus:border-indigo-500 focus:ring-indigo-500'} focus:ring-1 rounded-lg px-4 py-2.5 text-white placeholder-slate-600 transition-colors`} value={formData.carrierPhone} onChange={(e) => { handleChange(e); if (fieldErrors.carrierPhone) setFieldErrors(prev => { const { carrierPhone, ...rest } = prev; return rest; }); }} placeholder="(555) 123-4567" />
                                                    {fieldErrors.carrierPhone && <p className="text-xs text-red-400 mt-1">{fieldErrors.carrierPhone}</p>}
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                                <div className="space-y-2">
                                                    <label htmlFor="driverName" className="text-sm font-medium text-slate-300">Driver Name</label>
                                                    <input id="driverName" name="driverName" type="text" className="w-full bg-slate-950 border border-slate-700 hover:border-slate-600 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded-lg px-4 py-2.5 text-white placeholder-slate-600 transition-colors" value={formData.driverName} onChange={handleChange} placeholder="Driver's Full Name" />
                                                </div>
                                                <div className="space-y-2">
                                                    <label htmlFor="trailerType" className="text-sm font-medium text-slate-300">Trailer Type</label>
                                                    <div className="relative">
                                                        <select id="trailerType" name="trailerType" className="w-full bg-slate-950 border border-slate-700 hover:border-slate-600 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded-lg px-4 py-2.5 text-white appearance-none transition-colors" value={formData.trailerType} onChange={handleChange}>
                                                            <option value="">Select Type...</option>
                                                            <option value="Van">Dry Van</option>
                                                            <option value="Reefer">Reefer</option>
                                                            <option value="Flatbed">Flatbed</option>
                                                            <option value="Other">Other</option>
                                                        </select>
                                                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Carrier visibility confirmation */}
                                    {formData.carrierName && (
                                        <p className="text-xs text-muted-foreground mt-2">
                                            {isEditingAppointment && changedFields.timeChanged
                                                ? 'Carrier will receive the updated arrival time automatically.'
                                                : isEditingAppointment
                                                    ? 'Carrier will automatically see the updated schedule.'
                                                    : 'The carrier will see this appointment in their schedule.'
                                            }
                                        </p>
                                    )}
                                </div>
                            </>
                        )}

                        <div className="h-px bg-border my-6" />

                        {/* ─── Step 4 — Optional Details (collapsible) ─── */}
                        <div className="mb-6">
                            <button
                                type="button"
                                onClick={() => setOptionalExpanded(!optionalExpanded)}
                                className="w-full text-base font-semibold text-muted-foreground flex items-center gap-2 hover:text-foreground transition-colors text-left mb-3"
                            >
                                <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/50 self-center">{isWarehouseUser ? 'Step 4' : 'Step 3'}</span>
                                <span className="text-muted-foreground/20">—</span>
                                <FileText className="w-4 h-4 text-muted-foreground/60" />
                                Optional Details
                                {optionalExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground/50 ml-auto" /> : <ChevronRight className="w-4 h-4 text-muted-foreground/50 ml-auto" />}
                            </button>

                            {optionalExpanded && (
                                <div className="space-y-4 animate-fade-in">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                        <div className="space-y-2">
                                            <label htmlFor="truckNumber" className="text-sm font-medium text-slate-300">Truck Number</label>
                                            <input id="truckNumber" name="truckNumber" type="text" className="w-full bg-slate-950 border border-slate-700 hover:border-slate-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 rounded-lg px-4 py-2.5 text-white placeholder-slate-600 transition-colors" value={formData.truckNumber} onChange={handleChange} placeholder="e.g., TRK-12345" />
                                        </div>
                                        <div className="space-y-2">
                                            <label htmlFor="trailerNumber" className="text-sm font-medium text-slate-300">Trailer Number</label>
                                            <input id="trailerNumber" name="trailerNumber" type="text" className="w-full bg-slate-950 border border-slate-700 hover:border-slate-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 rounded-lg px-4 py-2.5 text-white placeholder-slate-600 transition-colors" value={formData.trailerNumber} onChange={handleChange} placeholder="e.g., TRL-67890" />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                        <div className="space-y-2">
                                            <label htmlFor="estimatedPalletCount" className="text-sm font-medium text-slate-300">
                                                Est. Pallet Count {formData.selectedPOs.length > 1 && <span className="text-xs text-cyan-400">(aggregated)</span>}
                                            </label>
                                            <input id="estimatedPalletCount" name="estimatedPalletCount" type="number" className="w-full bg-slate-950 border border-slate-700 hover:border-slate-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 rounded-lg px-4 py-2.5 text-white placeholder-slate-600 transition-colors" value={formData.estimatedPalletCount || ''} onChange={handleChange} min="0" placeholder="0" />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label htmlFor="notes" className="text-sm font-medium text-slate-300">Notes</label>
                                        <textarea
                                            id="notes"
                                            name="notes"
                                            className="w-full bg-slate-950 border border-slate-700 hover:border-slate-600 focus:border-white focus:ring-1 focus:ring-white rounded-lg px-4 py-3 text-white placeholder-slate-600 transition-colors min-h-[100px]"
                                            value={formData.notes}
                                            onChange={handleChange}
                                            placeholder="Any special handling instructions, hazardous materials, or gate codes..."
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* ─── Submit Zone ─── */}
                        <div className="border-t border-border pt-5 mt-6">
                            {formData.startAt && formData.customerPoNumber && (() => {
                                const facilityName = warehouses.find(w => w.id === formData.warehouseId)?.name;
                                return (
                                    <p className="text-xs text-muted-foreground mb-3">
                                        Scheduling <span className="font-mono font-semibold text-foreground/80">{formData.customerPoNumber}</span>
                                        {facilityName && <> at <span className="font-semibold text-foreground/80">{facilityName}</span></>}
                                        {' '}&mdash; <span className="font-semibold text-foreground/80">{(() => {
                                            const tz = warehouses.find(w => w.id === formData.warehouseId)?.timeZone;
                                            if (formData.startUtc) {
                                                return formatInWarehouseTimeZone(formData.startUtc, tz, { weekday: 'short', month: 'short', day: 'numeric' });
                                            } else if (formData.startAt) {
                                                return formatLocalWallClock(formData.startAt, "ccc, LLL d");
                                            }
                                            return '';
                                        })()}</span>
                                    </p>
                                );
                            })()}
                            <div className="flex justify-end">
                                <button
                                    type="submit"
                                    disabled={isSubmitting || loading}
                                    className="btn btn-primary w-full sm:w-auto px-8 py-3 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm shadow-primary/20 hover:shadow-md hover:shadow-primary/30 transition-all"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            {isEditingAppointment ? 'Updating...' : 'Submitting...'}
                                        </>
                                    ) : (
                                        <>
                                            {isEditingAppointment ? 'Update Appointment' : 'Schedule Appointment'}
                                            <CheckCircle2 className="w-4 h-4" />
                                        </>
                                    )}
                                </button>
                            </div>
                            {/* UX-9: Change-impact preview (edit mode only) */}
                            {isEditingAppointment && (
                                <div className="text-[10px] text-muted-foreground mt-2 space-y-0.5">
                                    <p className="font-medium text-muted-foreground/80">Updates after saving:</p>
                                    <p>• Dock board refreshes</p>
                                    <p>• Calendar updates</p>
                                    {formData.carrierName && <p>• Carrier notified{changedFields.timeChanged ? ' of new arrival time' : ''}</p>}
                                </div>
                            )}
                        </div>
                    </form>
                </div>

                {/* RIGHT: Sidebar (4 cols) */}
                <div className="lg:col-span-4 space-y-4">
                    <div className="sticky top-6 space-y-4">
                        <AISlotSuggestions warehouseTimeZone={warehouses.find(w => w.id === formData.warehouseId)?.timeZone} />

                        {/* Smart Manifest Card */}
                        <div
                            className={`relative overflow-hidden rounded-xl border p-4 transition-all ${formData.customerPoNumber ? 'bg-card border-cyan-500/25' : 'bg-card/50 border-border'
                                }`}
                        >
                            <h3 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2 relative z-10">
                                🚀 Smart Manifest
                                {formData.customerPoNumber && (
                                    <span className="text-[10px] font-bold bg-cyan-500 text-white px-1.5 py-0.5 rounded-full">
                                        {formData.selectedPOs.length > 1 ? `${formData.selectedPOs.length} POs` : 'ACTIVE'}
                                    </span>
                                )}
                            </h3>

                            <div className="relative z-10">
                                {formData.customerPoNumber ? (
                                    <div className="space-y-3 animate-fade-in">
                                        <div className="p-3 bg-muted/50 rounded-lg border border-border">
                                            <div className="flex justify-between text-xs mb-1.5">
                                                <span className="text-muted-foreground">Weight</span>
                                                <span className="text-foreground font-mono font-bold">
                                                    {formData.totalWeight ? `${formData.totalWeight.toLocaleString()} lbs` : '12,500 lbs'}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2 p-2.5 bg-emerald-500/10 border border-emerald-500/15 rounded-lg">
                                            <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                                            <div>
                                                <p className="text-xs font-semibold text-emerald-400">Auto-Optimized</p>
                                                <p className="text-[10px] text-emerald-400/70 mt-0.5">
                                                    Dock assigned automatically based on availability.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-muted-foreground text-xs space-y-2.5">
                                        <p>
                                            Enter a PO to activate automatic dock assignment.
                                        </p>
                                        <div className="flex gap-4 text-[11px]">
                                            <span>&gt; 10k lbs → <span className="text-amber-400 font-semibold">Heavy</span></span>
                                            <span>&lt; 10k lbs → <span className="text-emerald-400 font-semibold">Standard</span></span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
